import { Bell, Headphones, Lock, Mic, Radio } from 'lucide-react';
import { useMemo, useRef, type PointerEvent } from 'react';
import type { KeyConfig, Swatch } from '@bccomms/shared';
import { parseTarget } from '@bccomms/shared';
import { session } from '../lib/session';
import { keyVolume, targetLabel, useStore } from '../store';

const TAP_MS = 250;

export function KeyTile({ keyConfig: key, index }: { keyConfig: KeyConfig; index: number }) {
  const channels = useStore((s) => s.channels);
  const users = useStore((s) => s.users);
  const me = useStore((s) => s.user);
  const presence = useStore((s) => s.presence);
  const talking = useStore((s) => s.talking);
  const voxOn = useStore((s) => s.voxKeys.includes(key.id));
  const listening = useStore((s) => s.listening);
  const latched = useStore((s) => s.latched.includes(key.id));
  const calls = useStore((s) => s.calls);
  const vol = useStore((s) => s.settings.keyVolumes[key.id] ?? 0.8);
  const feeding = useStore((s) => s.feeding);
  const hotkeys = useStore((s) => s.settings.hotkeys);
  const downAt = useRef(0);

  const primary = useMemo(() => {
    const p = parseTarget(key.targets[0]);
    if (p?.kind === 'ch') return channels.find((c) => c.id === p.id) ?? null;
    return null;
  }, [key.targets, channels]);
  const primaryUser = useMemo(() => {
    const p = parseTarget(key.targets[0]);
    return p?.kind === 'user' ? (users.find((u) => u.id === p.id) ?? null) : null;
  }, [key.targets, users]);

  const isGroup = key.targets.length > 1;
  const label = key.label ?? (isGroup ? 'GROUP' : (primary?.name ?? primaryUser?.displayName ?? '?'));
  const code = isGroup ? key.targets.map((t) => targetLabel(t, true)).join(' ') : (primary?.short ?? (primaryUser ? 'DIRECT' : ''));
  const color: Swatch = key.color ?? primary?.color ?? primaryUser?.color ?? 'cyan';
  const isIfb = primary?.kind === 'ifb';
  const isProgram = primary?.kind === 'program';
  const canFeed = isProgram && !!me?.canFeedProgram && key.talk !== 'none';
  const isFeeding = isProgram && !!primary && feeding.includes(primary.id);

  const isTalking = key.targets.some((t) => talking.includes(t));
  const listenOn = key.listen === 'always' || key.targets.every((t) => listening.includes(t));
  const now = Date.now();
  const callFlash = key.targets.some((t) => (calls[t] ?? 0) > now);

  // Who is coming in on this key: others talking to one of my listened targets, or directly to me.
  const talkers = useMemo(() => {
    const out: string[] = [];
    for (const p of Object.values(presence)) {
      if (!p.online || p.userId === me?.id) continue;
      const hit = key.targets.some((t) => {
        const parsed = parseTarget(t);
        if (parsed?.kind === 'user') return parsed.id === p.userId && p.talking.includes(`user:${me?.id}`);
        return p.talking.includes(t) && listening.includes(t);
      });
      if (hit) out.push(users.find((u) => u.id === p.userId)?.displayName ?? '?');
    }
    return out;
  }, [presence, key.targets, listening, me?.id, users]);

  const feeders = useMemo(() => {
    if (!primary) return 0;
    const pid = primary.kind === 'ifb' ? primary.programChannelId : primary.kind === 'program' ? primary.id : null;
    if (!pid) return 0;
    return Object.values(presence).filter((p) => p.online && p.feeding.includes(pid)).length;
  }, [presence, primary]);
  const dimmed = isIfb && talkers.length > 0;

  // ---- TALK pointer handling: momentary / latch / both (tap = latch, hold = momentary) ----
  const onDown = (e: PointerEvent<HTMLButtonElement>) => {
    if (key.talk === 'none' || isProgram) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    downAt.current = performance.now();
    if (key.talk === 'latch') return; // acts on release
    if (key.talk === 'both' && latched) return; // tap will unlatch on release
    void session.ptt(key, true);
  };
  const onUp = (e: PointerEvent<HTMLButtonElement>) => {
    if (key.talk === 'none' || isProgram) return;
    e.preventDefault();
    const held = performance.now() - downAt.current;
    if (key.talk === 'latch') {
      void session.latch(key);
      return;
    }
    if (key.talk === 'both') {
      if (latched) {
        if (held < TAP_MS) void session.latch(key); // unlatch
        return;
      }
      if (held < TAP_MS) {
        void session.latch(key); // latch on (already talking)
        return;
      }
    }
    void session.ptt(key, false);
  };

  return (
    <div className="key" data-sw={color} data-talking={isTalking} data-incoming={talkers.length > 0} data-call={callFlash} style={{ ['--i' as string]: index }}>
      <div className="key__led" />
      <div className="key__head">
        <div>
          <div className="key__label" title={label}>
            {label}
          </div>
          <div className="key__code">{code}</div>
        </div>
        {hotkeys && index < 10 && <span className="key__hot">{(index + 1) % 10}</span>}
      </div>
      <div className="key__badges">
        {isIfb && <span className="key__badge">IFB</span>}
        {isProgram && <span className="key__badge">PROGRAM</span>}
        {(isIfb || isProgram) && feeders > 0 && <span className="key__badge key__badge--pgm">PGM LIVE</span>}
        {dimmed && <span className="key__badge key__badge--dim">DIM {primary?.dimDb} dB</span>}
        {isGroup && <span className="key__badge">GROUP</span>}
        {key.vox && <span className={`key__badge ${voxOn ? 'key__badge--in' : ''}`}>VOX{voxOn ? ' •' : ''}</span>}
        {talkers.length > 0 && <span className="key__badge key__badge--in">IN</span>}
      </div>
      <div className={`key__talkers ${talkers.length ? '' : 'key__talkers--idle'}`} title={talkers.join(', ')}>
        {talkers.length ? talkers.join(', ') : listenOn ? 'quiet' : 'muted'}
      </div>
      <button
        type="button"
        className={`key__talk ${isTalking || isFeeding ? 'key__talk--on' : ''} ${key.talk === 'none' || (isProgram && !canFeed) ? 'key__talk--none' : ''}`}
        onPointerDown={onDown}
        onPointerUp={onUp}
        onPointerCancel={() => key.talk !== 'none' && !isProgram && !latched && void session.ptt(key, false)}
        onClick={() => canFeed && primary && void session.toggleProgram(primary.id)}
        onContextMenu={(e) => e.preventDefault()}
        aria-pressed={isTalking || isFeeding}
        disabled={key.talk === 'none' || (isProgram && !canFeed)}
      >
        {isProgram ? <Radio size={18} /> : latched ? <Lock size={18} /> : <Mic size={18} />}
        {isProgram ? (isFeeding ? 'FEEDING' : 'FEED') : key.talk === 'none' ? 'LISTEN' : 'TALK'}
        {key.talk !== 'none' && !isProgram && <span className="key__talk__mode">{key.talk === 'momentary' ? 'PTT' : key.talk === 'latch' ? 'LATCH' : 'PTT · TAP'}</span>}
      </button>
      <div className="key__row">
        <button
          type="button"
          className={`key__listen ${listenOn ? 'key__listen--on' : ''} ${key.listen !== 'toggle' ? 'key__listen--locked' : ''}`}
          onClick={() => key.listen === 'toggle' && void session.listen(key, !listenOn)}
          disabled={key.listen === 'none'}
          aria-pressed={listenOn}
          title={key.listen === 'always' ? 'Always listening' : key.listen === 'none' ? 'Talk only' : 'Toggle listen'}
        >
          <Headphones size={14} />
          {key.listen === 'none' ? 'TALK ONLY' : listenOn ? 'LISTEN' : 'MUTED'}
        </button>
        {key.talk !== 'none' && !isProgram && (
          <button type="button" className="key__call" aria-label="Call" title="Send call signal" onClick={() => void session.call(key)}>
            <Bell size={14} />
          </button>
        )}
      </div>
      {key.listen !== 'none' && (
        <input
          className="key__vol"
          type="range"
          min={0}
          max={1}
          step={0.02}
          value={vol}
          aria-label="Listen volume"
          onChange={(e) => session.setKeyVolume(key.id, Number(e.target.value))}
          onDoubleClick={() => session.setKeyVolume(key.id, keyVolume('__default__'))}
        />
      )}
    </div>
  );
}
